/**
 * The store's places as paths on store.unityrp.it (nginx sends every path there to
 * store.html): the catalogue, a category, a package, the thanks after paying.
 *
 * On any other host the store is previewed at /store.html, where a path would leave it:
 * there the place rides in `?p=` instead.
 */

import { useEffect, useState, type MouseEvent } from 'react'

/** The page's own file when previewed (/store.html), or null on the store's host. */
const PREVIEW = /\.html$/.test(window.location.pathname) ? window.location.pathname : null

export type Route =
  | { name: 'home' }
  | { name: 'category'; id: number }
  | { name: 'package'; id: number }
  | { name: 'thanks' }
  | { name: 'missing' }

function currentPath(): string {
  if (PREVIEW) return new URLSearchParams(window.location.search).get('p') ?? '/'
  return window.location.pathname
}

function parse(path: string): Route {
  const clean = path.replace(/\/+$/, '') || '/'
  if (clean === '/') return { name: 'home' }
  if (clean === '/grazie') return { name: 'thanks' }
  const category = /^\/categoria\/(\d+)(?:-[\w-]*)?$/.exec(clean)
  if (category) return { name: 'category', id: Number(category[1]) }
  const item = /^\/pacchetto\/(\d+)(?:-[\w-]*)?$/.exec(clean)
  if (item) return { name: 'package', id: Number(item[1]) }
  return { name: 'missing' }
}

/** A readable tail for a path: "/pacchetto/12-vip-oro". */
export function slug(id: number, name: string): string {
  const words = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
  return words ? `${id}-${words}` : String(id)
}

export const packagePath = (pkg: { id: number; name: string }) =>
  `/pacchetto/${slug(pkg.id, pkg.name)}`
export const categoryPath = (category: { id: number; name: string }) =>
  `/categoria/${slug(category.id, category.name)}`

export function href(path: string): string {
  if (!PREVIEW) return path
  return path === '/' ? PREVIEW : `${PREVIEW}?p=${encodeURIComponent(path)}`
}

/** The same place as a whole address, for Tebex to send the player back to. */
export function absolute(path: string): string {
  return new URL(href(path), window.location.origin).toString()
}

const MOVED = 'store:navigate'

export function navigate(path: string, { replace = false } = {}) {
  if (replace) window.history.replaceState(null, '', href(path))
  else window.history.pushState(null, '', href(path))
  window.dispatchEvent(new Event(MOVED))
  window.scrollTo({ top: 0 })
}

/** A click on a link to one of the store's places: moves without reloading. */
export function follow(path: string) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigate(path)
  }
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parse(currentPath()))
  useEffect(() => {
    const update = () => setRoute(parse(currentPath()))
    window.addEventListener('popstate', update)
    window.addEventListener(MOVED, update)
    return () => {
      window.removeEventListener('popstate', update)
      window.removeEventListener(MOVED, update)
    }
  }, [])
  return route
}
